import { auth } from './auth0.service';
import EventEmitter from 'utils/EventEmitter';

const {
  REACT_APP_AUTH0_USER_SCOPE,
  REACT_APP_AUTH0_AUDIENCE_DOMAIN,
} = process.env;

// EventEmitter to coordinate concurrent refresh attempts
const refreshTokenEmitter = new EventEmitter();
let isRefreshing = false;

// Check if a JWT token is expired or will expire soon
export const isTokenExpired = (token: string, threshold = 5000): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + threshold;
  } catch {
    return true;
  }
};

// Wait for ongoing token refresh to complete
const waitForNewToken = (): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    refreshTokenEmitter.addListener(
      'refresh',
      (payload: string | Error) => {
        if (typeof payload === 'string') {
          resolve(payload);
        } else {
          reject(payload);
        }
      }
    );
  });
};

// Acquire new token using refresh token
const acquireNewToken = (refreshToken: string): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    auth.client.oauthToken(
      {
        refreshToken,
        grantType: 'refresh_token',
        scope: REACT_APP_AUTH0_USER_SCOPE,
        audience: REACT_APP_AUTH0_AUDIENCE_DOMAIN, // Use the audience domain for refresh
      },
      (err, result) => {
        if (result) {
          const { refreshToken: newRefreshToken, accessToken } = result;

          // Update tokens in localStorage
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }
          localStorage.setItem('accessToken', accessToken);

          // Emit event to notify waiting requests
          refreshTokenEmitter.emit('refresh', accessToken);
          // Remove all listeners since all receivers have received the event
          refreshTokenEmitter.removeAllListener();

          resolve(accessToken);
        } else {
          // Emit error to waiting requests
          refreshTokenEmitter.emit('refresh', err);
          refreshTokenEmitter.removeAllListener();
          reject(err);
        }
      }
    );
  });
};

// Main function to refresh token with concurrent request handling
export const refreshAccessToken = async (): Promise<string> => {
  let accessToken = '';

  if (!isRefreshing) {
    isRefreshing = true;
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      accessToken = await acquireNewToken(refreshToken);
    } finally {
      isRefreshing = false;
    }
  } else {
    // If already refreshing, wait for the current refresh to complete
    accessToken = await waitForNewToken();
  }

  return accessToken;
};

// Set up automatic token refresh
export const setupTokenRefresh = () => {
  // Check every minute if token needs refresh
  const checkInterval = setInterval(async () => {
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) {
      clearInterval(checkInterval);
      return;
    }

    if (isTokenExpired(accessToken)) {
      try {
        await refreshAccessToken();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        // Clear interval to stop checking
        clearInterval(checkInterval);

        // Clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }
  }, 60000); // Check every minute

  // Return cleanup function
  return () => clearInterval(checkInterval);
};